import styles from "./auth-page.module.css";
import { useEffect, useState } from "react";
import { Auth } from "@supabase/auth-ui-react";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { usePaths } from "BEDROCK/paths";
import { useProjectStore } from "entity-store/project-store/use-project-store";
import { ResetPasswordPage } from "./reset-password-page";
import { UIIcon } from "PosterEditor/common-components/ui-icon-drawing/ui-icon";
import { useAnalyticsTracker } from "analytics-tracker";
import { track } from "@amplitude/analytics-browser";

interface AuthPageProps {}

export const AuthPage = (props: AuthPageProps) => {
  const { saveGuestProjectToUsersAccount, refetchProjects } =
    useProjectStore();
  const { session, client: supabaseClient, signOut } = useAuthContext();
  const { landing, auth, mapEditor, navigateTo } = usePaths();
  const [isRecovery, setIsRecovery] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const { trackEvent } = useAnalyticsTracker();

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const type = hashParams.get("type");

    const checkSession = async () => {
      if (supabaseClient) {
        const { data } = await supabaseClient.auth.getSession();
        setIsAuthenticated(!!data.session);
        setIsRecovery(type === "recovery");
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get("redirect");

    if (redirect) {
      localStorage.setItem("postAuthRedirect", redirect);
    }
  }, []);

  useEffect(() => {
    // Wait for Supabase to handle the session from URL
    supabaseClient?.auth.getSession().then(() => {
      // Clean up the URL
      if (window.location.hash.includes("access_token")) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
      }
    });
  }, []);

  useEffect(() => {
    if (session) {
      trackEvent("User Signed In");
      refetchProjects().then(async () => {
        // Migrate guest project to authenticated account
        const newID = await saveGuestProjectToUsersAccount();

        const redirect = localStorage.getItem("postAuthRedirect") ?? undefined;
        if (redirect) {
          if (newID) {
            // Replace the old project ID in the redirect URL with the new one
            const url = new URL(redirect, window.location.origin);
            const pathParts = url.pathname.split("/");
            pathParts[pathParts.length - 1] = newID;
            url.pathname = pathParts.join("/");

            const newPath = mapEditor + url.pathname + url.search;

            localStorage.removeItem("postAuthRedirect");
            navigateTo(newPath);
          } else {
            localStorage.removeItem("postAuthRedirect");
            navigateTo(redirect);
          }
        } else if (newID) {
          // No redirect set but we have a migrated project - go to it
          navigateTo(`${mapEditor}/${newID}`);
        }
      });
    }
  }, [session]);

  if (loading) return null;

  if (isRecovery) {
    return <ResetPasswordPage />;
  }

  if (session) {
    navigateTo(landing);
  }

  const redirect = process.env.REACT_APP_BASE_REDIRECT_URL + auth;
  return (
    <div className={styles.container}>
      <div className={styles.innerContainer}>
        <InfoBox />
        <div className={styles.formContainer}>
          <Auth
            supabaseClient={supabaseClient!}
            providers={["google", "apple"]}
            onlyThirdPartyProviders={false}
            redirectTo={redirect}
            appearance={{
              theme: ThemeSupa,
              variables: {
                default: {
                  colors: {
                    brand: "#ff5476", // primary buttons
                    brandAccent: "#ff5476", // hover/focus color
                    inputBorder: "#e5e5e5",
                    inputBackground: "#f7f7f7",
                    messageText: "#21211f",
                    anchorTextColor: "#ff5476",
                  },
                  radii: {
                    borderRadiusButton: "8px",
                    inputBorderRadius: "6px",
                  },
                  fontSizes: {
                    baseBodySize: "15px",
                    baseInputSize: "14px",
                  },
                },
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};

const InfoBox = () => {
  const params = new URLSearchParams(window.location.search);
  const hasStartedProject = params.get("startedProject") === "true";

  if (hasStartedProject) {
    return <ContinueProjectInfo />;
  }

  return <GenericInfo />;
};

const ContinueProjectInfo = () => {
  return (
    <div className={styles.instructionContainer}>
      <div className={styles.textContainer}>
        <span>
          Please <b>create an account</b> or <b>sign in</b> to save and
          customise
          <br />
          your project
        </span>
      </div>
      {/* <UIIcon iconName="PENCIL" size={60} variant="SELECTED" /> */}
    </div>
  );
};

const GenericInfo = () => {
  return (
    <div className={styles.instructionContainer}>
      <div className={styles.textContainer}>
        <span>
          <b>Sign in</b> or <b>create an account</b> to start making custom
          printable <br />
          trail maps
        </span>
      </div>
      {/* <UIIcon iconName="MAP" size={60} variant="SELECTED" /> */}
    </div>
  );
};
